import React, { useCallback } from 'react';
import { CodeSnippetViewer } from '../../../questions';
import { getLanguageFromSkills } from '../../../utils/languageDetection';
import {
  MultiOptionsQuestion,
  SingleOptionQuestion,
  TrueFalseQuestion,
  ShortTextQuestion,
  LongTextQuestion,
  CodeCompletionQuestion,
  CodeRefactoringQuestion,
  CodeExplanationQuestion,
  FindTheBugQuestion,
  SecurityTaskQuestion,
  PerformanceOptimizationQuestion,
  CodeWriteQuestion,
  AudioAnswerQuestion,
  VideoAnswerQuestion
} from '../../../questions';

/**
 * QuestionRenderer - Renders the appropriate question component based on type
 * 
 * Memoization strategy:
 * - Uses useCallback to memoize onChange handler
 * - Only depends on questionId (not entire question object) to maintain stability
 * - This prevents video/audio components from re-rendering during parent updates
 */

// Question component mapping
const QUESTION_COMPONENTS = {
  'multiple-answer': MultiOptionsQuestion,
  'single-answer': SingleOptionQuestion,
  'true-false': TrueFalseQuestion,
  'long-text': LongTextQuestion,
  'short-text': ShortTextQuestion,
  'audio-answer': AudioAnswerQuestion,
  'video-answer': VideoAnswerQuestion,
  'code-completion': CodeCompletionQuestion,
  'code-explanation': CodeExplanationQuestion,
  'code-refactoring': CodeRefactoringQuestion,
  'code-write': CodeWriteQuestion,
  'find-the-bug': FindTheBugQuestion,
  'security-task': SecurityTaskQuestion,
  'performance-optimization': PerformanceOptimizationQuestion
};

const QuestionRenderer = React.memo(({ 
  question, 
  currentResponse, 
  handleChange 
}) => {
  // Memoize onChange to prevent creating new function references on each render
  // Extract questionId to avoid depending on entire question object (which may be recreated)
  const questionId = question?.id;
  const memoizedOnChange = useCallback((value) => {
    if (questionId !== undefined) {
      handleChange(questionId, value);
    }
  }, [handleChange, questionId]);
  
  if (!question) return null;
  
  // Determine if this is a followup question and get the appropriate data
  const isFollowup = !question.is_main_question;
  const followupData = isFollowup ? question.followup_questions_and_answers[question.current_followup_index] : null;
  
  // Get the question type, text, and code snippet based on whether it's main or followup
  let questionType = isFollowup ? followupData.type : question.type;
  let questionText = isFollowup ? followupData.question_text : question.questionText;
  let questionDetails = isFollowup ? followupData.questionDetails : question.questionDetails;
  let codeSnippet = isFollowup ? followupData.codeSnippet : question.codeSnippet;
  let skillsTested = isFollowup ? followupData.skillsTested : question.skillsTested;
  
  return (
    <>
      <p className="text-xl mb-4 text-gray-700 font-regular">{questionText}</p>
      
      {/* Display question details if available */}
      {questionDetails && (
        <p className="text-lg mb-4 text-gray-600">{questionDetails}</p>
      )}
      
      {/* Display code snippet if available for the current question being displayed */}
      {codeSnippet && (
        <div className="mb-4">
          <CodeSnippetViewer 
            code={codeSnippet}
            skillsTested={skillsTested}
          />
        </div>
      )}
      
      {/* Render the appropriate question component based on type */}
      <div className="mt-4 pl-4 border-l-4 border-blue-200 py-1">
        {(() => {
          const QuestionComponent = QUESTION_COMPONENTS[questionType];
          if (!QuestionComponent) return null;
          
          return (
            <QuestionComponent 
              key={`${question.id}-${isFollowup ? question.current_followup_index : 'main'}`}
              question={question} 
              value={currentResponse || (questionType === 'multiple-answer' ? [] : '')} 
              onChange={memoizedOnChange}
              disabled={false}
            />
          );
        })()}
      </div>
    </>
  );
});

export default QuestionRenderer;
